import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { FormikProps } from 'formik';
import { toast } from 'react-toastify';
import { EcwFormValues } from '../EcwConstants';
import useRecordUsage from '../../../Hooks/useRecordUsage';
import { RecordUsageRes } from '../../../redux/recordUsage/recordUsageApiSlice';
import { Form, InputGroup, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import { CommonEcwComponentProps, RouteFieldOptions, SiteFieldOptions } from '../EcwConstants';
import BarcodeInfo from '../InfoComponents/BarcodeInfo';
import { useDispatch } from 'react-redux';
import { ecwSelectedOrderInfo, setEcwSelectedBarcodeData } from '../../../redux/ecwOrder/ecwOrderSlice';
import ItemNameWarningDialog from '../InfoComponents/ItemNameWarningDialog';
import { useAppSelector } from '../../../redux/hooks';

interface EcwUsageDetailsFormProps extends CommonEcwComponentProps {
    formik: FormikProps<EcwFormValues>;
    isSelectedOrderProcessed: boolean;
}

const EcwUsageDetailsForm: React.FC<EcwUsageDetailsFormProps> = ({ formik, setLoading, isSelectedOrderProcessed }) => {
    const dispatch = useDispatch();
    const barcodeTimeoutRef = useRef<NodeJS.Timeout>();
    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const { getBarcodeDetailsAPI, getBarcodeDetailsLoading } = useRecordUsage();
    const selectedOrderInfo = useAppSelector(ecwSelectedOrderInfo);

    const [barcodeData, setBarcodeData] = useState<RecordUsageRes | null>(null);
    const [useUnitsList, setUseUnitsList] = useState<any[]>([]);
    const [isItemNameWarning, setIsItemNameWarning] = useState<boolean>(false);

    const { values, errors, touched, setFieldValue, handleChange, handleBlur, setFieldTouched, setFieldError, setSubmitting } = formik;

    useEffect(() => {
        setLoading?.(getBarcodeDetailsLoading);
    }, [getBarcodeDetailsLoading]);

    useEffect(() => {
        if (!values.barcode) {
            setBarcodeData(null);
            dispatch(setEcwSelectedBarcodeData(null));
            setUseUnitsList([]);
        }
    }, [values.barcode]);

    useEffect(() => {
        setFieldValue("itemInfo", {
            lotNumber: barcodeData?.lotNo ?? "",
            expiration: barcodeData?.expDate ?? "",
            ndcCode: barcodeData?.ndc ?? "",
            manufacturer: barcodeData?.manufacturer ?? "",
            cvXCode: barcodeData?.cvx ?? "",
            mvxCode: barcodeData?.mvxCode ?? "",
            mvxManufacturer: barcodeData?.mvxManufacturer ?? "",
            stock: barcodeData?.list2 ?? "",
            itemName: barcodeData?.rxName ?? "",
            itemType: barcodeData?.list1 ?? "",
        });
    }, [barcodeData]);

    const handleBarcodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            setFieldTouched("barcode", true, true);
            return;
        };


        const missingFields = [];
        if (!values.patID) missingFields.push('patID');
        if (!values.orderId) missingFields.push('orderId');

        if (missingFields.length) {
            missingFields.forEach(field => setFieldTouched(field, true));
            return;
        }

        // Clear previous timeout
        if (barcodeTimeoutRef.current) {
            clearTimeout(barcodeTimeoutRef.current);
        }

        // Debounce barcode API call
        barcodeTimeoutRef.current = setTimeout(async () => {
            try {
                const res: RecordUsageRes = await getBarcodeDetailsAPI({
                    ...values,
                    query: "true",
                });

                if (res.status === "F") {
                    // toast.error(res.msg);
                    setFieldError("barcode", res.msg);
                    setFieldTouched("barcode", true, false);
                    setSubmitting(true);
                    return;
                }

                setSubmitting(false);

                if (selectedOrderInfo?.medicationRequest) {
                    if (selectedOrderInfo.medicationRequest?.medicationName !== res?.rxName) {
                        setIsItemNameWarning(true);
                    }
                }

                /* Here in API response we get unitName as unitCode, thats why using unitName for both value and label */
                const updatedUseUnitsList = res?.useUnits?.map(({ unitName, isDefault }) => ({
                    value: unitName,
                    label: unitName,
                    isDefault: !!isDefault,
                })) ?? [];

                const defaultUnit = updatedUseUnitsList.find(u => u.isDefault) || updatedUseUnitsList[0] || null;

                setBarcodeData(res);
                dispatch(setEcwSelectedBarcodeData(res));
                setUseUnitsList(updatedUseUnitsList);
                setFieldValue("units", defaultUnit?.value || "");

                toast.success(res.msg);

            } catch (error) {
                console.error("Barcode lookup failed:", error);
            }
        }, 500);
    }

    const handleItemNameWarningAction = useCallback((action: "accept" | "cancel") => {
        if (action === "accept") {
            setIsItemNameWarning(false);
        } else {
            setIsItemNameWarning(false);
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        }
    }, []);

    const quantityPopover = useMemo(() => (
        <Popover id="quantityPopover">
            <Popover.Body>
                <Table hover={true}>
                    <thead>
                        <tr>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Location</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barcodeData?.inventory && barcodeData.inventory.length > 0 ? (
                            barcodeData.inventory.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{item?.netQty}</td>
                                    <td>{barcodeData?.uom}</td>
                                    <td>{item?.locationName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', color: 'red' }}>
                                    No inventory found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Popover.Body>
        </Popover>
    ), [barcodeData?.inventory]);

    return (
        <div className='item-usage-info-box'>
            <h4 className="form-section-title">Enter Item Usage Details</h4>
            <Form.Group className='form-field'>
                <Form.Label>Barcode*</Form.Label>
                <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Enter Barcode"
                    name="barcode"
                    value={values.barcode}
                    onChange={handleChange}
                    onBlur={handleBarcodeBlur}
                    isInvalid={touched.barcode && !!errors.barcode}
                    disabled={isSelectedOrderProcessed}
                />
                {errors.barcode && touched.barcode && (
                    <Form.Control.Feedback type="invalid">{errors.barcode}</Form.Control.Feedback>
                )}
            </Form.Group>
            <div className='form-field-wrapper'>
                <Form.Group className='form-field'>
                    <Form.Label>Quantity*</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="number"
                            placeholder="Enter Quantity"
                            name="qty"
                            value={values.qty}
                            onChange={handleChange}
                            onBlur={(e) => {
                                if (!isItemNameWarning) {
                                    handleBlur(e);
                                }
                            }}
                            isInvalid={touched.qty && !!errors.qty}
                            disabled={isSelectedOrderProcessed}
                        />
                        {useUnitsList?.length > 0 && (
                            <Form.Select
                                aria-label="Select Quantity"
                                name="units"
                                className="unit-select"
                                value={values.units}
                                onChange={handleChange}
                                disabled={isSelectedOrderProcessed}
                                title={useUnitsList?.find(item => item.value === values.units)?.label || ""}
                            >
                                {useUnitsList.map((unit: any, index: number) => (
                                    <option key={index} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </InputGroup>
                    {errors.qty && touched.qty && (
                        <Form.Control.Feedback type="invalid">{errors.qty}</Form.Control.Feedback>
                    )}
                    {barcodeData?.inventory && (
                        <OverlayTrigger trigger="hover" placement="bottom" overlay={quantityPopover}>
                            <Form.Text id="quantity-hint" muted>Inventory</Form.Text>
                        </OverlayTrigger>
                    )}
                </Form.Group>
                <Form.Group className='form-field'>
                    <Form.Label>Waste</Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Enter Waste"
                        name="waste"
                        value={values.waste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.waste && !!errors.waste}
                        disabled={isSelectedOrderProcessed}
                    />
                    {errors.waste && touched.waste && (
                        <Form.Control.Feedback type="invalid">{errors.waste}</Form.Control.Feedback>
                    )}
                </Form.Group>
            </div>
            <Form.Group className='form-field'>
                <Form.Label>Route*</Form.Label>
                <Select
                    name='route'
                    options={RouteFieldOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select route"
                    isDisabled={isSelectedOrderProcessed}
                    onChange={(selectedItem) => setFieldValue("route", selectedItem?.value)}
                    onBlur={() => setFieldTouched("route", true, true)}
                    value={RouteFieldOptions.find(item => item.value === values.route) || null}
                    className={`${touched.route && errors.route ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.route && touched.route && (
                    <Form.Control.Feedback type="invalid">{errors.route}</Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group className='form-field'>
                <Form.Label>Site*</Form.Label>
                <Select
                    name='site'
                    options={SiteFieldOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select site"
                    isDisabled={isSelectedOrderProcessed}
                    onChange={(selectedItem) => setFieldValue("site", selectedItem?.value)}
                    onBlur={() => setFieldTouched("site", true, true)}
                    value={SiteFieldOptions.find(item => item.value === values.site) || null}
                    className={`${touched.site && errors.site ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.site && touched.site && (
                    <Form.Control.Feedback type="invalid">{errors.site}</Form.Control.Feedback>
                )}
            </Form.Group>

            <BarcodeInfo barcodeInfo={barcodeData} />

            {isItemNameWarning && (
                <ItemNameWarningDialog
                    show={isItemNameWarning}
                    onAccept={() => handleItemNameWarningAction("accept")}
                    onCancel={() => handleItemNameWarningAction("cancel")}
                    orderItemName={selectedOrderInfo?.medicationRequest?.medicationName ?? ""}
                    barcodeItemName={barcodeData?.rxName ?? ""}
                />
            )}
        </div>
    )
}

export default EcwUsageDetailsForm;
